import { t } from 'i18next';
import { AMENITY_CODES } from '@data/types/Amenity';
import { LEGACY_AMENITY_CATEGORY_MAP, } from '@data/types/LegacyAmenity';
export function getLegacyAmenityCategory(code) {
    var _a;
    const trimmedCode = code === null || code === void 0 ? void 0 : code.trim().toLowerCase();
    if (!trimmedCode) {
        return 'additional-amenity';
    }
    return ((_a = LEGACY_AMENITY_CATEGORY_MAP.get(trimmedCode)) !== null && _a !== void 0 ? _a : 'additional-amenity');
}
export function filterMappedAmenities(amenities) {
    return amenities.filter((amenity) => AMENITY_CODES.includes(amenity.code));
}
export function normalizeAmenitiesFromDTO(dtos) {
    const AMENITY_CODE_RENAMES = {
        'alfresco-dinning': 'alfresco-dining',
        'electic-vehicle-charger': 'electric-vehicle-charger',
    };
    return dtos.map((dto) => {
        var _a, _b;
        const normalizedCode = (_a = dto.code) === null || _a === void 0 ? void 0 : _a.trim().toLowerCase();
        const code = (_b = AMENITY_CODE_RENAMES[normalizedCode]) !== null && _b !== void 0 ? _b : normalizedCode;
        return {
            category: dto.categoryCode,
            code,
            isTop: dto.isTop,
            // Custom amenities have no code and rely on name for display;
            // standard amenities use i18n keys based on code, so name is cleared.
            name: code ? '' : dto.name,
        };
    });
}
export function sortAmenities(amenities, { alphabetically, byTopAmenities } = {}) {
    const res = [...amenities];
    if (alphabetically) {
        res.sort((a, b) => {
            const aName = (a.code ? t(`amenities/${a.code}`) : a.name) || '';
            const bName = (b.code ? t(`amenities/${b.code}`) : b.name) || '';
            return aName.localeCompare(bName);
        });
    }
    if (byTopAmenities) {
        res.sort((a, b) => {
            const aIsTop = a.isTop ? 1 : 0;
            const bIsTop = b.isTop ? 1 : 0;
            return bIsTop - aIsTop;
        });
    }
    return res;
}
export function getCardAmenities(amenities, maxVisible = 5) {
    // Deduplicate by code (skip custom amenities that have no code)
    const seen = new Set();
    const unique = amenities.filter((a) => {
        if (!a.code)
            return false;
        if (seen.has(a.code))
            return false;
        seen.add(a.code);
        return true;
    });
    // Keep only amenities that have an icon mapping
    const mapped = filterMappedAmenities(unique);
    // Sort: isTop first, then alphabetically
    const sorted = sortAmenities(mapped, {
        alphabetically: true,
        byTopAmenities: true,
    });
    // If only 1 overflows, show it as an icon instead of a "+1 more" pill
    const overflow = sorted.length - maxVisible;
    if (overflow <= 1) {
        return {
            overflowCount: 0,
            visible: sorted,
        };
    }
    return {
        overflowCount: overflow,
        visible: sorted.slice(0, maxVisible),
    };
}
export function getAmenitiesByCategory(amenities) {
    // Make sure top amenities are always first in the list
    const arr = amenities.map((x) => (Object.assign(Object.assign({}, x), { category: x.category && x.isTop ? 'top-amenities' : x.category })));
    const sortedAmenities = sortAmenities(arr, { byTopAmenities: true });
    const grouped = sortedAmenities.reduce((acc, amenity) => {
        const category = amenity.category
            ? amenity.category
            : getLegacyAmenityCategory(amenity.code);
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(amenity);
        return acc;
    }, {});
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
        if (a === 'top-amenities')
            return -1;
        if (b === 'top-amenities')
            return 1;
        if (a === 'additional-amenity')
            return 1;
        if (b === 'additional-amenity')
            return -1;
        const aName = t(`amenity-category-${a}`) || a;
        const bName = t(`amenity-category-${b}`) || b;
        return aName.localeCompare(bName);
    });
    const result = {};
    for (const key of sortedKeys) {
        result[key] = grouped[key];
    }
    return result;
}
